import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { QRCodeComponent } from 'angularx-qrcode';
import { DialogComponent } from 'src/app/shared/modules/components/dialog/dialog.component';
import { InlineAlertComponent } from 'src/app/shared/modules/components/inline-alert/inline-alert.component';
import { AccountService } from 'src/app/account/account.service';
import { AuthorizeService } from 'src/api-authorization/authorize.service';
import { SiteDataService } from 'src/app/shared/core/services/site-data.service';
import { ConfirmMFARequest, MFAMethod, UserMFASettings } from 'src/app/shared/models/shared.model';

type InterstitialStage = 'choose' | 'setup' | 'recovery';

@Component({
	selector: 'app-mfa-enrollment-interstitial',
	standalone: true,
	imports: [CommonModule, FormsModule, QRCodeComponent, DialogComponent, InlineAlertComponent],
	providers: [AccountService],
	templateUrl: './mfa-enrollment-interstitial.component.html',
	styleUrls: ['./mfa-enrollment-interstitial.component.scss'],
})
export class MfaEnrollmentInterstitialComponent implements OnInit, OnDestroy {
	private accountService = inject(AccountService);
	private authorizeService = inject(AuthorizeService);
	private siteData = inject(SiteDataService);
	private router = inject(Router);
	private unsubscribe$ = new Subject<void>();

	MFAMethod = MFAMethod;

	visible = false;
	stage: InterstitialStage = 'choose';
	method: MFAMethod | null = null;
	loading = false;
	errorMessage: string | null = null;

	otpAuthUri: string | null = null;
	secretKey: string | null = null;
	email: string | null = null;
	code = '';

	recoveryCodes: string[] = [];
	emailResendCooldown = 0;
	private cooldownTimer: ReturnType<typeof setInterval> | null = null;

	// This component is mounted once at app root, outside the router outlet, so a single instance
	// serves every login in the tab and signing out never destroys it. Track who the wall was last
	// shown for so enrollment state is never carried from one user to the next.
	private shownForUserID: number | null | undefined;

	ngOnInit(): void {
		this.siteData.loggedInUser$
			.pipe(takeUntil(this.unsubscribe$))
			.subscribe(session => {
				const required = !!session?.mfaEnrollmentRequired;
				const userID = session?.userID ?? null;

				// Any change of identity, and any opening or closing of the wall, starts from a clean
				// slate: recovery codes must never outlive the dialog that displayed them.
				if (required !== this.visible || userID !== this.shownForUserID) {
					this.resetEnrollment();
				}

				this.shownForUserID = userID;
				this.visible = required;
			});
	}

	private resetEnrollment(): void {
		this.stage = 'choose';
		this.method = null;
		this.code = '';
		this.errorMessage = null;
		this.otpAuthUri = null;
		this.secretKey = null;
		this.email = null;
		this.recoveryCodes = [];
		this.loading = false;
		this.emailResendCooldown = 0;
		if (this.cooldownTimer) {
			clearInterval(this.cooldownTimer);
			this.cooldownTimer = null;
		}
	}

	chooseMethod(method: MFAMethod): void {
		this.method = method;
		this.code = '';
		this.errorMessage = null;
		this.startSetup(method);
	}

	private startSetup(method: MFAMethod): void {
		this.loading = true;
		this.errorMessage = null;
		this.accountService
			.initializeMfaSetup({ activeMFAMethod: method } as UserMFASettings)
			.pipe(takeUntil(this.unsubscribe$))
			.subscribe({
				next: response => {
					this.loading = false;
					this.stage = 'setup';
					this.otpAuthUri = response.otpSetupUri;
					this.secretKey = response.otpSetupKey;
					this.email = response.email;
					if (method === MFAMethod.EMAIL) {
						this.startEmailCooldown();
					}
				},
				error: () => {
					this.loading = false;
					this.errorMessage = 'There was a problem starting MFA setup. Please try again.';
				},
			});
	}

	resendEmailCode(): void {
		if (this.emailResendCooldown > 0 || this.method !== MFAMethod.EMAIL) {
			return;
		}
		this.startSetup(MFAMethod.EMAIL);
	}

	private startEmailCooldown(): void {
		this.emailResendCooldown = 30;
		if (this.cooldownTimer) {
			clearInterval(this.cooldownTimer);
		}
		this.cooldownTimer = setInterval(() => {
			this.emailResendCooldown = Math.max(0, this.emailResendCooldown - 1);
			if (this.emailResendCooldown === 0 && this.cooldownTimer) {
				clearInterval(this.cooldownTimer);
				this.cooldownTimer = null;
			}
		}, 1000);
	}

	verifyCode(): void {
		if (!this.code || this.method === null) {
			return;
		}
		this.loading = true;
		this.errorMessage = null;
		const request: ConfirmMFARequest = { code: this.code.trim(), method: this.method };
		this.accountService
			.completeMfaSetup(request)
			.pipe(takeUntil(this.unsubscribe$))
			.subscribe({
				next: response => {
					this.loading = false;
					if (response.succeeded) {
						this.recoveryCodes = response.recoveryCodes ?? [];
						this.stage = 'recovery';
					} else {
						this.errorMessage = response.errorMessage || 'That code was not valid. Please try again.';
					}
				},
				error: () => {
					this.loading = false;
					this.errorMessage = 'That code was not valid. Please try again.';
				},
			});
	}

	copyRecoveryCodes(): void {
		navigator.clipboard?.writeText(this.recoveryCodes.join('\n'));
	}

	acknowledgeAndRelease(): void {
		// The backend re-issued the cookie on completion (C1). Re-fetch session/settings so
		// mfaEnrollmentRequired flips false and this interstitial closes, then toast against the
		// revealed dashboard. Only report success once the refreshed session actually clears the
		// requirement; if the cookie re-issue silently failed (rare RefreshSignInAsync error) or the
		// re-fetch errors, keep the interstitial up and direct the user to sign out rather than
		// closing on a false success.
		this.loading = true;
		this.errorMessage = null;
		this.siteData
			.getLoggedInUserObservable()
			.pipe(takeUntil(this.unsubscribe$))
			.subscribe({
				next: (session: { mfaEnrollmentRequired?: boolean } | undefined) => {
					this.loading = false;
					if (session?.mfaEnrollmentRequired === false) {
						this.siteData.showSiteSuccessAlert('MFA setup completed successfully.');
					} else {
						this.errorMessage = 'Your MFA is set up, but we could not refresh your session. '
							+ 'Please sign out and sign back in to continue.';
					}
				},
				error: () => {
					this.loading = false;
					this.errorMessage = 'Your MFA is set up, but we could not refresh your session. '
						+ 'Please try again, or sign out and sign back in.';
				},
			});
	}

	backToChoose(): void {
		this.resetEnrollment();
	}

	signOut(): void {
		// One-click sign out — skip the logout confirmation page; the wall is itself a hard stop.
		// Hide immediately: loggedInUser$ retains its stale (still-required) value after logout, so
		// without this the modal lingers over the login page.
		this.visible = false;
		this.resetEnrollment();
		this.authorizeService.signOut().subscribe({
			next: () => this.router.navigate(['/account/login']),
			error: () => this.router.navigate(['/account/login']),
		});
	}

	ngOnDestroy(): void {
		if (this.cooldownTimer) {
			clearInterval(this.cooldownTimer);
		}
		this.unsubscribe$.next();
		this.unsubscribe$.complete();
	}
}
